import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import DSBox from "views/system-design/DSBox";
import DSTypography from "views/system-design/DSTypography";
import DSCircularProgress from "views/system-design/DSCircularProgress";
import DSButton from "views/system-design/DSButton";
import DSChip from "views/system-design/DSChip";
import DSTable from "views/system-design/Table/DSTable";
import DSTableHead from "views/system-design/Table/DSTableHead";
import DSTableBody from "views/system-design/Table/DSTableBody";
import DSTableRow from "views/system-design/Table/DSTableRow";
import DSTableCell from "views/system-design/Table/DSTableCell";
import DSTableContainer from "views/system-design/Table/DSTableContainer";
import DSDialog from "views/system-design/DSDialog";
import DSDialogTitle from "views/system-design/DSDialogTitle";
import DSDialogContent from "views/system-design/DSDialogContent";
import DSDialogActions from "views/system-design/DSDialogActions";
import DSAlert from "views/system-design/DSAlert";
import PageHeader from "components/PageHeader";
import { extractApiErrorMessage } from "../../../utils/util";
import firebaseService from "../../../firebase_service/firebaseService";
import { FeatureBetaGroup } from "../../../api/admin";
import {
  FEATURE_BETA_GROUPS_QUERY_KEY,
  useDeleteFeatureBetaGroup,
  useFeatureBetaGroups,
  useSendFeatureBetaGroupUpdate,
  useUpdateFeatureBetaGroup,
} from "../hooks/useFeatureBetaGroups";
import FeatureBetaGroupModal from "./FeatureBetaGroupModal";
import GroupMembersDialog from "./GroupMembersDialog";

const FeatureBetaGroups = () => {
  const {
    data: groups,
    isLoading,
    isFetching,
    isError,
    error,
  } = useFeatureBetaGroups();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [editing, setEditing] = useState<FeatureBetaGroup | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [viewingMembers, setViewingMembers] = useState<FeatureBetaGroup | null>(
    null,
  );
  const [confirmingSend, setConfirmingSend] = useState<FeatureBetaGroup | null>(
    null,
  );
  const [showAddDevicesInfo, setShowAddDevicesInfo] = useState(false);
  // Outcome of the last action (send / delete / pause), success or failure.
  const [notice, setNotice] = useState<string | null>(null);

  const { mutate: update } = useUpdateFeatureBetaGroup();
  const { mutate: remove } = useDeleteFeatureBetaGroup();
  const { mutate: sendUpdate, isPending: isSending } =
    useSendFeatureBetaGroupUpdate();

  const publicVersion = firebaseService.general?.public;

  const openCreate = () => {
    setEditing(null);
    setIsModalOpen(true);
  };

  const openEdit = (group: FeatureBetaGroup) => {
    setEditing(group);
    setIsModalOpen(true);
  };

  const handleDelete = (group: FeatureBetaGroup) => {
    if (
      window.confirm(
        `Delete "${group.name}"? Its ${group.memberCount} clocks go back to their normal target version.`,
      )
    ) {
      remove(group.id, {
        onError: (e) =>
          setNotice(extractApiErrorMessage(e, "Failed to delete the group")),
      });
    }
  };

  const handleSend = () => {
    if (!confirmingSend) return;
    const group = confirmingSend;
    sendUpdate(group.id, {
      onSuccess: ({ sent, failed }) => {
        setConfirmingSend(null);
        setNotice(
          failed > 0
            ? `Queued an update on ${sent} clocks in "${group.name}". ${failed} failed — you can safely retry.`
            : `Queued an update on ${sent} clocks in "${group.name}".`,
        );
      },
      onError: (e) => {
        setConfirmingSend(null);
        setNotice(
          `Failed to send: ${e instanceof Error ? e.message : "Unknown error"}`,
        );
      },
    });
  };

  return (
    <DSBox>
      <PageHeader
        overline="Devices"
        title="Feature Beta Groups"
        subtitle="Roll a version out gradually: clocks in a group update to max(their normal target, the group version), so a group never pulls a clock backwards. A hard version group pins its clocks to exactly its version"
      />

      {isLoading ? (
        <DSBox sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
          <DSCircularProgress />
        </DSBox>
      ) : isError ? (
        <DSBox sx={{ p: 2 }}>
          <DSTypography color="error">
            Failed to load groups:{" "}
            {error instanceof Error ? error.message : "Unknown error"}
          </DSTypography>
        </DSBox>
      ) : (
        <DSBox>
          <DSBox
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              mb: 2,
              alignItems: "center",
              gap: 1,
            }}
          >
            {isFetching && <DSCircularProgress size={18} />}
            <DSButton variant="contained" size="small" onClick={openCreate}>
              New group
            </DSButton>
            <DSButton
              variant="outlined"
              size="small"
              onClick={() => setShowAddDevicesInfo(true)}
            >
              Add devices to a group
            </DSButton>
            <DSButton
              variant="outlined"
              size="small"
              disabled={isFetching}
              onClick={() =>
                queryClient.invalidateQueries({
                  queryKey: FEATURE_BETA_GROUPS_QUERY_KEY,
                })
              }
            >
              Refresh
            </DSButton>
          </DSBox>

          <DSTableContainer>
            <DSTable size="small">
              <DSTableHead>
                <DSTableRow>
                  <DSTableCell>Name</DSTableCell>
                  <DSTableCell>Version</DSTableCell>
                  <DSTableCell>Clocks</DSTableCell>
                  <DSTableCell>Description</DSTableCell>
                  <DSTableCell align="right">Actions</DSTableCell>
                </DSTableRow>
              </DSTableHead>
              <DSTableBody>
                {groups?.map((group) => {
                  // Once public passes the group version the group stops doing
                  // anything - max() picks public - so it is flagged rather
                  // than silently lingering as a dead row.
                  // A hard group pins rather than raises, so it is never a no-op.
                  const superseded =
                    !group.hardVersion &&
                    publicVersion != null &&
                    group.version <= publicVersion;
                  return (
                    <DSTableRow
                      key={group.id}
                      hover
                      onClick={() => setViewingMembers(group)}
                      sx={{
                        opacity: group.active ? 1 : 0.5,
                        cursor: "pointer",
                      }}
                    >
                      <DSTableCell>
                        <DSBox
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <DSTypography variant="body2" fontWeight={500}>
                            {group.name}
                          </DSTypography>
                          {!group.active && (
                            <DSChip size="small" label="paused" />
                          )}
                          {group.hardVersion && (
                            <DSChip
                              size="small"
                              label="hard version"
                              color="warning"
                              variant="outlined"
                            />
                          )}
                          {superseded && group.active && (
                            <DSChip
                              size="small"
                              label="superseded"
                              color="default"
                            />
                          )}
                        </DSBox>
                      </DSTableCell>
                      <DSTableCell>{group.version}</DSTableCell>
                      <DSTableCell>{group.memberCount}</DSTableCell>
                      <DSTableCell>
                        <DSTypography variant="caption">
                          {group.description || "—"}
                        </DSTypography>
                      </DSTableCell>
                      {/* The actions have their own clicks, so they must not
                          also open the clocks dialog through the row. */}
                      <DSTableCell
                        align="right"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <DSBox
                          sx={{
                            display: "flex",
                            gap: 1,
                            justifyContent: "flex-end",
                          }}
                        >
                          <DSButton
                            size="small"
                            variant="outlined"
                            disabled={!group.memberCount}
                            onClick={() => setConfirmingSend(group)}
                          >
                            Send update
                          </DSButton>
                          <DSButton
                            size="small"
                            variant="outlined"
                            onClick={() => openEdit(group)}
                          >
                            Edit
                          </DSButton>
                          <DSButton
                            size="small"
                            variant="outlined"
                            onClick={() =>
                              update(
                                {
                                  id: group.id,
                                  data: { active: !group.active },
                                },
                                {
                                  onError: (e) =>
                                    setNotice(
                                      extractApiErrorMessage(
                                        e,
                                        "Failed to update the group",
                                      ),
                                    ),
                                },
                              )
                            }
                          >
                            {group.active ? "Pause" : "Resume"}
                          </DSButton>
                          <DSButton
                            size="small"
                            color="error"
                            onClick={() => handleDelete(group)}
                          >
                            Delete
                          </DSButton>
                        </DSBox>
                      </DSTableCell>
                    </DSTableRow>
                  );
                })}
              </DSTableBody>
            </DSTable>
          </DSTableContainer>
        </DSBox>
      )}

      <FeatureBetaGroupModal
        open={isModalOpen}
        group={editing}
        onClose={() => setIsModalOpen(false)}
      />

      <GroupMembersDialog
        open={!!viewingMembers}
        group={viewingMembers}
        onClose={() => setViewingMembers(null)}
      />

      <DSDialog
        open={!!confirmingSend}
        onClose={() => setConfirmingSend(null)}
        maxWidth="xs"
        fullWidth
      >
        <DSDialogTitle>Send update</DSDialogTitle>
        <DSDialogContent>
          <DSTypography variant="body2" sx={{ mb: 2 }}>
            You are about to queue an app update on{" "}
            {confirmingSend?.memberCount} devices in "{confirmingSend?.name}",
            targeting version {confirmingSend?.version}.
          </DSTypography>
          <DSAlert severity="error">
            This action cannot be undone. The clocks will download and install
            as soon as they pick up the command.
          </DSAlert>
        </DSDialogContent>
        <DSDialogActions sx={{ justifyContent: "flex-end", px: 3, pb: 2 }}>
          <DSButton size="small" onClick={() => setConfirmingSend(null)}>
            Cancel
          </DSButton>
          <DSButton
            variant="contained"
            size="small"
            disabled={isSending}
            onClick={handleSend}
          >
            Submit
          </DSButton>
        </DSDialogActions>
      </DSDialog>

      <DSDialog
        open={showAddDevicesInfo}
        onClose={() => setShowAddDevicesInfo(false)}
        maxWidth="xs"
        fullWidth
      >
        <DSDialogTitle>Add devices to a group</DSDialogTitle>
        <DSDialogContent>
          <DSTypography variant="body2" sx={{ mb: 1 }}>
            Devices are added to a beta group from the Devices table. You will
            be redirected there. Then:
          </DSTypography>
          <DSBox sx={{ typography: "body2", "& ol": { pl: 2, m: 0 } }}>
            <ol>
              <li>Search or filter for the devices you want to add.</li>
              <li>
                Check them (or use &quot;All&quot; / &quot;Select&quot; for many
                devices).
              </li>
              <li>Click &quot;Commands&quot;.</li>
              <li>Click &quot;Add to Beta Group&quot; and pick the group.</li>
            </ol>
          </DSBox>
        </DSDialogContent>
        <DSDialogActions sx={{ justifyContent: "flex-end", px: 3, pb: 2 }}>
          <DSButton size="small" onClick={() => setShowAddDevicesInfo(false)}>
            Cancel
          </DSButton>
          <DSButton
            variant="contained"
            size="small"
            onClick={() => navigate("/admin/devices")}
          >
            Go to Devices
          </DSButton>
        </DSDialogActions>
      </DSDialog>

      <DSDialog
        open={!!notice}
        onClose={() => setNotice(null)}
        maxWidth="xs"
        fullWidth
      >
        <DSDialogContent>
          <DSTypography variant="body2">{notice}</DSTypography>
        </DSDialogContent>
        <DSDialogActions sx={{ justifyContent: "flex-end", px: 3, pb: 2 }}>
          <DSButton
            variant="contained"
            size="small"
            onClick={() => setNotice(null)}
          >
            Close
          </DSButton>
        </DSDialogActions>
      </DSDialog>
    </DSBox>
  );
};

export default FeatureBetaGroups;
